import { motion } from "framer-motion";
import { Shirt, ShieldCheck, Archive, Layers } from "lucide-react";
import { Eyebrow, MaskLine } from "./Reveal";
import { EASE } from "@/lib/brand";

const Tile = ({ className = "", delay = 0, children, testid }) => (
  <motion.div
    data-testid={testid}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, ease: EASE, delay }}
    whileHover={{ y: -4 }}
    className={`group relative overflow-hidden rounded-[24px] border border-white/10 bg-[#0B0F15] ${className}`}
  >
    {children}
  </motion.div>
);

const Feature = ({ icon: Icon, title, text }) => (
  <div className="relative z-10 flex h-full flex-col justify-between p-7">
    <span className="h-11 w-11 rounded-full border border-white/15 grid place-items-center text-[var(--mth-blue)] transition-colors group-hover:bg-[var(--mth-blue)] group-hover:text-white">
      <Icon className="h-5 w-5" />
    </span>
    <div>
      <h3 className="font-display font-extrabold uppercase text-xl sm:text-2xl">{title}</h3>
      <p className="mt-2 text-sm text-slate-300 max-w-xs">{text}</p>
    </div>
  </div>
);

export const Bento = () => (
  <section id="craft" data-testid="bento-section" className="bg-[#07090D] py-24 md:py-36">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <Eyebrow>03 — Built properly</Eyebrow>
      <h2 className="mt-5 mb-14 font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.92] tracking-tight">
        <MaskLine inView>Comfort that</MaskLine>
        <MaskLine inView delay={0.1}>supports <span className="text-[var(--mth-blue)]">more.</span></MaskLine>
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-[240px] gap-4 md:gap-5">
        <Tile testid="bento-fabric" className="md:col-span-7 md:row-span-2 min-h-[420px] md:min-h-0">
          <img src="/images/detail.jpg" alt="Close-up of hoodie drawstrings and logo" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
          <div className="absolute bottom-0 p-7 sm:p-9">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--mth-blue-soft)]">Premium fleece</p>
            <p className="mt-3 font-display font-black text-5xl sm:text-7xl leading-none">80<span className="text-[var(--mth-blue)]">/</span>20</p>
            <p className="mt-3 text-slate-200 max-w-sm">80% cotton, 20% polyester. Soft on the inside, built to last on the outside.</p>
          </div>
        </Tile>
        <Tile testid="bento-hood" delay={0.1} className="md:col-span-5"><Feature icon={Layers} title="Double-lined hood" text="Two layers for structure and warmth that holds its shape." /></Tile>
        <Tile testid="bento-pocket" delay={0.15} className="md:col-span-5"><Feature icon={Archive} title="Kangaroo pocket" text="Deep front pocket — hands, phone, keys, all sorted." /></Tile>
        <Tile testid="bento-cuffs" delay={0.1} className="md:col-span-4"><Feature icon={Shirt} title="Ribbed cuffs & hem" text="Stays put, keeps the cold out, keeps the fit clean." /></Tile>
        <Tile testid="bento-message" delay={0.2} className="md:col-span-8">
          <img src="/images/city.jpg" alt="Back print of the hoodie: Talk. Connect. Keep going." className="absolute inset-0 h-full w-full object-cover object-[50%_40%] transition-transform duration-[1.4s] group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/50 to-transparent" />
          <div className="relative z-10 flex h-full flex-col justify-center p-7 sm:p-9">
            <ShieldCheck className="h-6 w-6 text-[var(--mth-blue)]" />
            <p className="mt-4 font-display font-black uppercase text-2xl sm:text-4xl leading-tight">Talk.<br />Connect.<br />Keep going.</p>
            <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-slate-300">The message on every back</p>
          </div>
        </Tile>
      </div>
    </div>
  </section>
);
